import React, { useEffect, useRef, useContext } from "react";
import KrishnaImg from "../assets/—Pngtree—lord krishna flute and peacock_17776225.png";
import { Link } from "react-router-dom";
import {
  FaFire, FaTruck, FaShieldAlt, FaStar, FaUsers, FaGift,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import HeroSlider from "../components/HeroSlider";
import Footer from "../components/Footer";
import ThemeContext from "../contexts/ThemeContext";
import festivalimg from "../assets/festival.jpeg"

// ===== FIREWORKS CANVAS =====
const FireworksCanvas = ({ isDark }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animId;
    let fireworks = [];
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const randomColor = () => {
      const colors = [
        "#ff4444", "#ff8800", "#ffdd00", "#44ff44",
        "#44ddff", "#ff44dd", "#ffffff", "#ff6644",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    };

    class Particle {
      constructor(x, y, color) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.vx = (Math.random() - 0.5) * 6;
        this.vy = (Math.random() - 0.5) * 6;
        this.alpha = 1;
        this.radius = Math.random() * 3 + 1;
        this.gravity = 0.08;
        this.decay = Math.random() * 0.015 + 0.01;
      }
      update() {
        this.vx *= 0.98;
        this.vy *= 0.98;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.alpha -= this.decay;
      }
      draw() {
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.alpha);
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 8;
        ctx.shadowColor = this.color;
        ctx.fill();
        ctx.restore();
      }
    }

    class Firework {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height;
        this.targetY = Math.random() * (canvas.height * 0.55);
        this.color = randomColor();
        this.speed = Math.random() * 4 + 4;
        this.vy = -this.speed;
        this.exploded = false;
        this.trail = [];
      }
      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > 10) this.trail.shift();
        this.y += this.vy;
        if (this.y <= this.targetY) {
          this.explode();
          this.exploded = true;
        }
      }
      explode() {
        const count = Math.floor(Math.random() * 60) + 60;
        for (let i = 0; i < count; i++) {
          particles.push(new Particle(this.x, this.y, this.color));
        }
      }
      draw() {
        this.trail.forEach((p, i) => {
          ctx.save();
          ctx.globalAlpha = (i / this.trail.length) * 0.5;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.shadowBlur = 6;
          ctx.shadowColor = this.color;
          ctx.fill();
          ctx.restore();
        });
        ctx.save();
        ctx.beginPath();
        ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.color;
        ctx.fill();
        ctx.restore();
      }
    }

    const launch = () => {
      fireworks.push(new Firework());
    };
    launch();
    const interval = setInterval(launch, 800);

    const animate = () => {
      // Light mode = light fade bg, Dark mode = dark fade bg
      ctx.fillStyle = isDark
        ? "rgba(10, 14, 39, 0.18)"
        : "rgba(255, 245, 230, 0.25)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      fireworks = fireworks.filter((fw) => {
        fw.update();
        fw.draw();
        return !fw.exploded;
      });

      particles = particles.filter((p) => {
        p.update();
        p.draw();
        return p.alpha > 0;
      });

      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(interval);
      window.removeEventListener("resize", resize);
    };
  }, [isDark]); // re-run when theme changes

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0, left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        borderRadius: "24px",
      }}
    />
  );
};

// ===== ANIMATION STYLES =====
const getAnimationStyles = (isDark) => `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(40px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeInLeft {
    from { opacity: 0; transform: translateX(-40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeInRight {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes floatKrishna {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-14px); }
  }
  @keyframes flamePulse {
    0%, 100% { transform: scale(1); }
    50%       { transform: scale(1.18); }
  }
  .reveal {
    opacity: 0; transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .reveal-left {
    opacity: 0; transform: translateX(-40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal-left.visible { opacity: 1; transform: translateX(0); }
  .reveal-right {
    opacity: 0; transform: translateX(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal-right.visible { opacity: 1; transform: translateX(0); }
  .stagger-child {
    opacity: 0; transform: translateY(30px);
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  .stagger-child.visible { opacity: 1; transform: translateY(0); }
  .krishna-float { animation: floatKrishna 4s ease-in-out infinite; }
  .hero-title { animation: fadeInLeft 0.9s ease forwards; }
  .hero-desc  { animation: fadeInLeft 1.1s ease 0.2s both; }
  .hero-btn {
    animation: fadeInLeft 1.1s ease 0.4s both;
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
  }
  .hero-btn:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 20px rgba(220,53,69,0.4) !important;
  }
  .stat-card {
    transition: transform 0.25s ease, box-shadow 0.25s ease !important;
    cursor: default;
  }
  .stat-card:hover {
    transform: translateY(-6px) !important;
    box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
  }
  .why-card {
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease !important;
    border: 2px solid transparent !important;
    cursor: default;
  }
  .why-card:hover {
    transform: translateY(-6px) !important;
    box-shadow: 0 10px 28px rgba(220,53,69,0.15) !important;
    border-color: #dc3545 !important;
  }
  .why-card:hover .icon-flame { animation: flamePulse 0.5s ease infinite; }
  .festival-img-wrap { overflow: hidden; border-radius: 0.5rem; }
  .festival-img-wrap img { transition: transform 0.5s ease; }
  .festival-img-wrap:hover img { transform: scale(1.04); }
  .explore-btn {
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
  }
  .explore-btn:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 18px rgba(220,53,69,0.35) !important;
  }

  /* Hero fireworks section - THEME BASED */
  .hero-fireworks-section {
    position: relative;
    border-radius: 24px;
    overflow: hidden;
    padding: 3rem 2rem;
    margin-bottom: 2rem;
    transition: background 0.4s ease;
    background: ${
      isDark
        ? "linear-gradient(135deg, #0a0e27 0%, #1a0a2e 50%, #0a1a0e 100%)"
        : "linear-gradient(135deg, #fff8f0 0%, #fff0f8 50%, #f0fff4 100%)"
    };
  }

  .hero-fireworks-content {
    position: relative;
    z-index: 1;
  }

  .hero-fireworks-section .hero-title {
    color: ${isDark ? "#fff" : "#dc3545"} !important;
    text-shadow: ${isDark ? "0 0 30px rgba(255,100,100,0.5)" : "none"};
  }

  .hero-fireworks-section .hero-desc {
    color: ${isDark ? "#ddd" : "#444"} !important;
  }

  @media (max-width: 576px) {
    .hero-title { font-size: 2rem !important; }
    .hero-desc  { font-size: 1rem !important; }
    .stat-number { font-size: 1.6rem !important; }
    .map-section { margin-bottom: 2rem !important; }
    .hero-fireworks-section { padding: 2rem 1rem; }
  }

  /* FIX: scroll-reveal IntersectionObserver animation was unreliable —
     cards could get stuck at opacity: 0 (fully invisible) on ANY screen
     size, not just mobile. Reliability matters more than the fade-in
     flourish, so we force all reveal/stagger elements to always be
     visible immediately, on every device. */
  .reveal, .reveal-left, .reveal-right, .stagger-child {
    opacity: 1 !important;
    transform: none !important;
  }
`;

const LandingPage = () => {
  const { isDark } = useContext(ThemeContext);

  return (
    <div className={`page-shell ${isDark ? 'bg-dark text-light' : ''}`}>
      <style>{getAnimationStyles(isDark)}</style>
      <Navbar />

      <main className="container py-4">

        {/* ===== HERO WITH FIREWORKS ===== */}
        <section className="hero-fireworks-section mb-5">
          <FireworksCanvas isDark={isDark} />

          <div className="hero-fireworks-content">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <h1 className="fw-bold text-danger display-4 mb-3 hero-title">
                  Welcome To AK Crackers 🪔
                </h1>
                <p className="lead hero-desc">
                  Premium Quality Sivakasi Crackers for every celebration.
                  Retail and Wholesale orders available with attractive offers.
                </p>
                <p className="mb-4 hero-desc">
                  We provide safe, colourful and exciting fireworks directly
                  from Sivakasi at the best price.
                </p>
                <Link to="/products" className="btn btn-danger btn-lg hero-btn">
                  Shop Now
                </Link>
              </div>

              <div className="col-lg-6 text-center mt-4 mt-lg-0 reveal-right">
                <img
                  src={KrishnaImg}
                  alt="Krishna"
                  className="img-fluid d-none d-lg-block krishna-float"
                  style={{
                    maxHeight: "420px",
                    filter: isDark
                      ? "drop-shadow(0 0 20px rgba(255,150,50,0.4))"
                      : "drop-shadow(0 0 16px rgba(220,53,69,0.3))",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Hero Slider */}
        <div className="reveal">
          <HeroSlider />
        </div>

        {/* Stats */}
        <section className="mb-5">
          <div className="row g-4">
            {[
              { icon: <FaUsers size={40} />, value: "1000+", label: "Happy Customers" },
              { icon: <FaGift size={40} />, value: "500+", label: "Cracker Products" },
              { icon: <FaStar size={40} />, value: "10+", label: "Years Experience" },
            ].map((item, i) => (
              <div className="col-md-4" key={i}>
                <div
                  className={`card text-center h-100 shadow-sm stat-card stagger-child ${isDark ? 'border-secondary' : ''}`}
                  style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}
                  data-delay={i * 150}
                >
                  <div className="card-body py-4">
                    <div className="text-danger mb-3">{item.icon}</div>
                    <h3 className={`fw-bold stat-number ${isDark ? 'text-light' : ''}`}>{item.value}</h3>
                    <p className={`mb-0 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>{item.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-5 reveal">
          <h2 className={`text-center fw-bold mb-4 ${isDark ? 'text-light' : ''}`}>Why Choose AK Crackers?</h2>
          <div className="row g-4">
            {[
              { icon: <FaFire size={40} className="text-danger mb-3 icon-flame" />, title: "Premium Quality", desc: "Best quality Sivakasi crackers." },
              { icon: <FaTruck size={40} className="text-danger mb-3" />, title: "Fast Delivery", desc: "Quick and safe delivery." },
              { icon: <FaShieldAlt size={40} className="text-danger mb-3" />, title: "Safe Products", desc: "Tested and quality assured." },
              { icon: <FaStar size={40} className="text-danger mb-3" />, title: "Trusted Brand", desc: "Loved by thousands of customers." },
            ].map((item, i) => (
              <div className="col-6 col-md-3" key={i}>
                <div
                  className={`card text-center h-100 shadow-sm why-card stagger-child ${isDark ? 'border-secondary' : ''}`}
                  style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}
                  data-delay={i * 100}
                >
                  <div className="card-body py-4">
                    {item.icon}
                    <h5 className={`fw-semibold ${isDark ? 'text-light' : ''}`}>{item.title}</h5>
                    <p className={`small mb-0 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Festival Banner */}
        <section className="mb-5 reveal">
          <div
            className={`card border-0 shadow-sm overflow-hidden festival-img-wrap ${isDark ? 'border-secondary' : ''}`}
            style={isDark ? { backgroundColor: '#1e1e1e' } : {}}
          >
            <img
              src={festivalimg}
              alt="Festival"
              className="img-fluid w-100"
              style={{ maxHeight: "340px", objectFit: "cover" }}
            />
            <div className="card-body text-center p-4">
              <h2 className="fw-bold text-danger">Celebrate Festivals With Joy ✨</h2>
              <p className={`lead mb-3 ${isDark ? 'text-light' : ''}`}>Light up every moment with premium AK Crackers.</p>
              <Link to="/products" className="btn btn-danger explore-btn">Explore Products</Link>
            </div>
          </div>
        </section>

        {/* Google Map */}
        <section className="mb-5 map-section reveal">
          <h2 className={`text-center fw-bold mb-4 ${isDark ? 'text-light' : ''}`}>Visit Our Location 📍</h2>
          <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-sm">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3900.2458770548265!2d77.9312760756053!3d9.459465026128293!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b04cf5452216e91%3A0x41d9f844f68f9584!2sSivakasi%2C%20Tamil%20Nadu%20626001!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              title="Google Map"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;